// BEGIN FILE: src/components/widgets/FamilyHouseRelationshipsWidget.tsx
/**
 * Family House Relationships Widget
 * 
 * Displays family members mapped to Vedic houses based on traditional Bhavat Bhavam principles.
 * Uses CACHED chart data from birth_profiles.chart_data.house_scores (saved by App.tsx).
 * 
 * NO CALCULATION LOGIC - purely reads from database cache for performance and consistency.
 * If cache is missing, prompts user to recalculate chart in Birth Details tab.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Box, Sheet, Typography, Chip, CircularProgress, IconButton, Modal, ModalDialog, ModalClose } from '@mui/joy';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import { supabase } from '../../utils/supabaseClient';
import { 
  HOUSE_TO_FAMILY, 
  getHouseFamilyTitle,
  type HouseNum 
} from '../../constants/familyHouseMapping';
import { calculateFamilyMemberLifeAreas, getChipColor } from '../../utils/familyLifeAreaScores';
import { 
  PLANET_OVERLAY_PREDICTIONS, 
  PLANET_ON_MOON_PREDICTIONS,
  type PlanetName,
  type OverlayPrediction 
} from '../../constants/planetOverlayPredictions';

// House descriptions for tooltips
const HOUSE_DESCRIPTIONS: Record<number, string> = {
  1: 'Self, Vitality, Body',
  2: 'Wealth, Family, Speech',
  3: 'Courage, Siblings, Effort',
  4: 'Home, Mother, Comforts',
  5: 'Children, Creativity, Intellect',
  6: 'Enemies, Debts, Health',
  7: 'Marriage, Partnerships, Public',
  8: 'Longevity, Transformations, Secrets',
  9: 'Fortune, Dharma, Higher learning',
  10: 'Career, Status, Authority',
  11: 'Gains, Networks, Aspirations',
  12: 'Losses, Expenditure, Moksha',
};

interface ChartOverlay {
  familyMemberName: string;
  relationship: string;
  planet: PlanetName;
  targetHouse: number; // Native's house
  targetIsMoon?: boolean; // Special case
  prediction: OverlayPrediction;
}

type BirthProfile = {
  id: string;
  owner_id: string;
  full_name: string | null;
  gender: string | null;
  relationship: string | null;
  dob_day: number | null;
  dob_month: number | null;
  dob_year: number | null;
  tob_hour: number | null;
  tob_minute: number | null;
  tob_second: number | null;
  place_label: string | null;
  place_lat: number | null;
  place_lng: number | null;
  place_timezone: string | null;
  chart_data?: any; // JSONB cached chart data
  chart_calculated_at?: string; // ISO timestamp
  family_of_profile_id?: string | null; // NEW: Links to primary profile
};

type Props = {
  userId: string;
  selectedProfileId?: string | null; // 'my_profile' for main user, profile ID for family members
  onNavigateToBirthDetails?: () => void;
  d1HouseScores?: Record<number, number> | null;
  bhavaBalaRupas?: Record<number, number> | null;
  astroData?: any;
};

export default function FamilyHouseRelationshipsWidget({ 
  userId, 
  selectedProfileId,
  onNavigateToBirthDetails, 
  d1HouseScores, 
  bhavaBalaRupas, 
  astroData 
}: Props) {
  // Widget should always load if we have a profile selected
  // (either 'my_profile' for regular users, or any profile for admins)
  const isViewingMainProfile = true;
  
  const [familyProfiles, setFamilyProfiles] = useState<BirthProfile[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [familyHouseScores, setFamilyHouseScores] = useState<Record<string, Record<number, number>>>({});
  const [chartOverlays, setChartOverlays] = useState<ChartOverlay[]>([]);
  const [cumulativeScores, setCumulativeScores] = useState<Record<number, { total: number; count: number; avg: number }> | null>(null);
  const [mainUserHouseScores, setMainUserHouseScores] = useState<Record<number, number> | null>(null);
  const [mainUserFullName, setMainUserFullName] = useState<string>('');
  const [matrixModalOpen, setMatrixModalOpen] = useState(false);

  // Calculate main user's house scores from prop
  useEffect(() => {
    if (!isViewingMainProfile || !d1HouseScores) {
      setMainUserHouseScores(null);
      return;
    }
    
    // Convert d1HouseScores to house-indexed format
    const houseScores: Record<number, number> = {};
    for (let house = 1; house <= 12; house++) {
      houseScores[house] = d1HouseScores[house] || 0;
    }
    setMainUserHouseScores(houseScores);
  }, [d1HouseScores, isViewingMainProfile]);

  // Get main user's full name
  useEffect(() => {
    if (!isViewingMainProfile || !userId) return;
    
    const fetchMainUserName = async () => {
      try {
        const { data, error } = await supabase
          .from('birth_profiles')
          .select('full_name')
          .eq('owner_id', userId)
          .is('relationship', null) // Main profile has no relationship
          .order('created_at', { ascending: true })
          .limit(1)
          .maybeSingle();
        
        if (data && data.full_name) {
          setMainUserFullName(data.full_name);
        }
      } catch (e) {
        console.error('[FamilyWidget] Error fetching main user name:', e);
      }
    };
    
    fetchMainUserName();
  }, [userId, isViewingMainProfile]);

  // Helper: Check if relationship is direct family (Mother, Father, Spouse, Children)
  const isDirectFamily = (relationship: string | null): boolean => {
    if (!relationship) return false;
    const direct = ['Mother', 'Father', 'Spouse', 'Son', 'Daughter'];
    return direct.includes(relationship);
  };

  const loadFamilyProfiles = useCallback(async () => {
    if (!userId || !isViewingMainProfile) return;
    setLoading(true);
    setError(null);
    
    try {
      // Determine the primary profile ID to query family members for
      let primaryProfileId: string | null = null;
      
      if (selectedProfileId && selectedProfileId !== 'my_profile') {
        // Admin viewing someone else's chart - use selected profile as primary
        primaryProfileId = selectedProfileId;
      } else {
        // Regular user or admin viewing own profile - find their Self profile
        const { data: selfProfile } = await supabase
          .from('birth_profiles')
          .select('id')
          .eq('owner_id', userId)
          .or('relationship.eq.Self,relationship.is.null')
          .order('created_at', { ascending: true })
          .limit(1)
          .single();
        
        if (selfProfile) {
          primaryProfileId = selfProfile.id;
        }
      }
      
      if (!primaryProfileId) {
        console.warn('[FamilyWidget] No primary profile found');
        setFamilyProfiles([]);
        setLoading(false);
        return;
      }
      
      // Query family members linked to this primary profile
      const { data, error: fetchError } = await supabase
        .from('birth_profiles')
        .select('id, owner_id, full_name, gender, relationship, dob_day, dob_month, dob_year, tob_hour, tob_minute, tob_second, place_label, place_lat, place_lng, place_timezone, chart_data, chart_calculated_at, family_of_profile_id')
        .eq('family_of_profile_id', primaryProfileId)
        .order('created_at', { ascending: false });
      
      if (fetchError) throw fetchError;
      
      // Filter out 'Other' relationship profiles (not family members)
      const filteredData = (data ?? []).filter(profile => 
        profile.relationship && profile.relationship !== 'Other'
      );
      
      console.log(`[FamilyWidget] Loaded ${filteredData.length} family members for primary profile: ${primaryProfileId}`);
      setFamilyProfiles(filteredData as BirthProfile[]);
    } catch (e: any) {
      console.error('[FamilyWidget] Error loading family profiles:', e);
      setError(e?.message || 'Failed to load family profiles.');
    } finally {
      setLoading(false);
    }
  }, [userId, selectedProfileId, isViewingMainProfile]);

  // Helper: Find family member by relationship
  const findFamilyMember = (relationshipDbValue: string): BirthProfile | undefined => {
    return familyProfiles.find(p => p.relationship === relationshipDbValue);
  };

  // Helper: Check if profile has complete birth data
  const hasCompleteData = (profile?: BirthProfile): boolean => {
    if (!profile) return false;
    return !!(
      profile.dob_day && profile.dob_month && profile.dob_year &&
      profile.tob_hour != null && profile.tob_minute != null &&
      profile.place_lat && profile.place_lng && profile.place_timezone
    );
  };

  // Calculate house scores for a family member
  // Uses cached chart_data.house_scores from database (saved by App.tsx)
  // Falls back to null if cache is missing or invalid

  useEffect(() => {
    if (isViewingMainProfile) {
      loadFamilyProfiles();
    }
  }, [loadFamilyProfiles, isViewingMainProfile]);

  // Listen for family profile updates
  useEffect(() => {
    if (!isViewingMainProfile) return;
    
    const handleRefresh = () => loadFamilyProfiles();
    window.addEventListener('family:refresh', handleRefresh);
    return () => window.removeEventListener('family:refresh', handleRefresh);
  }, [loadFamilyProfiles, isViewingMainProfile]);

  // Calculate house scores for each family member using CACHED DATA ONLY
  // No duplicated calculation logic - uses database cache saved by App.tsx
  const calculateMemberHouseScores = useCallback((profile: BirthProfile): Record<number, number> | null => {
    if (!isViewingMainProfile) return null;
    
    try {
      if (!hasCompleteData(profile)) return null;

      // Try to use cached chart data (saved by App.tsx with house_scores field)
      if (profile.chart_data && typeof profile.chart_data === 'object') {
        const cached = profile.chart_data as any;
        
        // Check for house_scores (with underscore - matches ChartData type)
        if (cached.house_scores && typeof cached.house_scores === 'object') {
          const keys = Object.keys(cached.house_scores);
          // Check if we have all 12 houses (keys might be strings "1"-"12" or numbers 1-12)
          if (keys.length === 12) {
            console.log(`[FamilyWidget] ✅ Using cached house_scores for ${profile.full_name}`);
            // Convert string keys to numbers if needed
            const normalized: Record<number, number> = {};
            for (const key of keys) {
              const houseNum = Number(key);
              if (houseNum >= 1 && houseNum <= 12) {
                normalized[houseNum] = cached.house_scores[key];
              }
            }
            return normalized;
          }
        }
        
        // Fallback: Try legacy houseScores field (camelCase - older cache format)
        if (cached.houseScores && typeof cached.houseScores === 'object') {
          const keys = Object.keys(cached.houseScores);
          if (keys.length === 12) {
            console.log(`[FamilyWidget] ✅ Using cached houseScores (legacy) for ${profile.full_name}`);
            const normalized: Record<number, number> = {};
            for (const key of keys) {
              const houseNum = Number(key);
              if (houseNum >= 1 && houseNum <= 12) {
                normalized[houseNum] = cached.houseScores[key];
              }
            }
            return normalized;
          }
        }
      }

      // No cache available - return null and show message to user
      console.warn(`[FamilyWidget] ⚠️ No cached house_scores for ${profile.full_name} - user needs to recalculate chart`);
      return null;
    } catch (e) {
      console.error('[FamilyWidget] Error reading cached house scores for', profile.full_name, ':', e);
      return null;
    }
  }, [isViewingMainProfile]);

  // Calculate scores for all family members
  useEffect(() => {
    if (!isViewingMainProfile) return;
    
    const calculateScores = async () => {
      const scores: Record<string, Record<number, number>> = {};
      
      console.log(`[FamilyWidget] Calculating scores for ${familyProfiles.length} family members`);
      
      for (const profile of familyProfiles) {
        console.log(`[FamilyWidget] Processing ${profile.full_name} (${profile.relationship})`);
        console.log(`[FamilyWidget] Has complete data: ${hasCompleteData(profile)}`);
        
        if (hasCompleteData(profile)) {
          const houseScores = calculateMemberHouseScores(profile);
          console.log(`[FamilyWidget] House scores for ${profile.full_name}:`, houseScores);
          if (houseScores) {
            scores[profile.id] = houseScores;
          }
        }
      }

      console.log(`[FamilyWidget] Final family house scores:`, scores);
      setFamilyHouseScores(scores);
    };

    calculateScores();
  }, [familyProfiles, calculateMemberHouseScores, isViewingMainProfile]);

  // Calculate cumulative family house scores (direct family only)
  useEffect(() => {
    if (!isViewingMainProfile || !astroData) return;
    
    const directFamilyScores: Record<number, number[]> = {};
    
    // IMPORTANT: Include main user's scores first (from d1HouseScores prop)
    if (mainUserHouseScores) {
      for (let house = 1; house <= 12; house++) {
        if (!directFamilyScores[house]) directFamilyScores[house] = [];
        directFamilyScores[house].push(mainUserHouseScores[house] || 0);
      }
    }
    
    // Then collect scores from direct family members
    for (const profile of familyProfiles) {
      if (!isDirectFamily(profile.relationship)) continue;
      
      const memberScores = familyHouseScores[profile.id];
      if (!memberScores) continue;
      
      for (let house = 1; house <= 12; house++) {
        if (!directFamilyScores[house]) directFamilyScores[house] = [];
        directFamilyScores[house].push(memberScores[house] || 0);
      }
    }
    
    // Calculate cumulative scores (total, count, average)
    const cumulative: Record<number, { total: number; count: number; avg: number }> = {};
    for (let house = 1; house <= 12; house++) {
      const scores = directFamilyScores[house] || [];
      const total = scores.reduce((sum, score) => sum + score, 0);
      const count = scores.length;
      const avg = count > 0 ? total / count : 0;
      cumulative[house] = { total, count, avg };
    }
    
    setCumulativeScores(cumulative);
  }, [familyProfiles, familyHouseScores, isViewingMainProfile, astroData, mainUserHouseScores]);

  // Calculate chart overlays (family planets on native's houses/Moon)
  useEffect(() => {
    if (!isViewingMainProfile || !astroData) return;
    
    const overlays: ChartOverlay[] = [];
    
    // Get native's Moon house
    const nativeMoonHouse = astroData.placements?.Moon?.house;
    
    // All planets to check (matching PlanetName type)
    const planetsToCheck: PlanetName[] = ['Sun', 'Moon', 'Mars', 'Mercury', 'Jupiter', 'Venus', 'Saturn', 'Rahu', 'Ketu'];
    
    // Check each direct family member's planets
    for (const profile of familyProfiles) {
      if (!isDirectFamily(profile.relationship)) continue;
      if (!profile.chart_data?.placements) continue;
      
      const familyPlacements = profile.chart_data.placements;
      const familyName = profile.full_name || profile.relationship || 'Family Member';
      
      // Check each planet
      for (const planetName of planetsToCheck) {
        const planetPlacement = familyPlacements[planetName];
        if (!planetPlacement || !planetPlacement.house) continue;
        
        const targetHouse = planetPlacement.house;
        
        // Check if planet is on native's Moon
        if (nativeMoonHouse && targetHouse === nativeMoonHouse) {
          const moonPrediction = PLANET_ON_MOON_PREDICTIONS[planetName];
          if (moonPrediction) {
            overlays.push({
              familyMemberName: familyName,
              relationship: profile.relationship || 'Unknown',
              planet: planetName,
              targetHouse: nativeMoonHouse,
              targetIsMoon: true,
              prediction: moonPrediction
            });
          }
        }
        
        // Check if planet falls in specific native's houses
        const housePredictions = PLANET_OVERLAY_PREDICTIONS[planetName];
        if (housePredictions) {
          const prediction = housePredictions[targetHouse];
          if (prediction) {
            overlays.push({
              familyMemberName: familyName,
              relationship: profile.relationship || 'Unknown',
              planet: planetName,
              targetHouse,
              targetIsMoon: false,
              prediction
            });
          }
        }
      }
    }
    
    setChartOverlays(overlays);
  }, [familyProfiles, isViewingMainProfile, astroData]);

  // Show helpful message if viewing a family member's chart (after all hooks)
  if (!isViewingMainProfile) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Sheet
          variant="soft"
          color="primary"
          sx={{
            p: 3,
            borderRadius: 'md',
            maxWidth: '600px',
            mx: 'auto',
          }}
        >
          <Typography level="title-lg" sx={{ mb: 1, color: 'primary.700' }}>
            Family Relationships View
          </Typography>
          <Typography level="body-md" sx={{ mb: 2, color: 'text.secondary' }}>
            This widget shows family members mapped to Vedic houses based on your main profile.
            To view your family relationships, please switch back to your main profile from the dropdown above.
          </Typography>
          <Typography level="body-sm" sx={{ fontStyle: 'italic', color: 'text.tertiary' }}>
            💡 Tip: Add family members via Birth Details tab → Select relationship → Save
          </Typography>
        </Sheet>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
        <CircularProgress size="md" />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2, color: 'danger.500' }}>
        <Typography level="body-sm">Error: {error}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* ===== NEW SECTION 1: Family House Scores Matrix ===== */}
      {cumulativeScores && Object.values(cumulativeScores).some(s => s.count > 0) && (
        <Sheet
          variant="outlined"
          sx={{
            borderRadius: 'md',
            p: 3,
            bgcolor: 'background.level1',
            border: '2px solid',
            borderColor: 'primary.300',
            position: 'relative',
          }}
        >
          {/* Expand Icon */}
          <IconButton
            variant="plain"
            color="primary"
            size="sm"
            onClick={() => setMatrixModalOpen(true)}
            sx={{
              position: 'absolute',
              top: 16,
              right: 16,
              zIndex: 1,
            }}
          >
            <OpenInFullIcon />
          </IconButton>

          <Typography level="h4" sx={{ mb: 2, color: 'primary.700', fontWeight: 700, pr: 5 }}>
            📊 Family House Scores Matrix
          </Typography>
          <Typography level="body-sm" sx={{ mb: 2, color: 'text.secondary' }}>
            House-wise strength analysis for self and direct family members (Mother, Father, Spouse, Children).
            Last row shows cumulative average scores across all family members.
          </Typography>
          
          {/* Matrix Table */}
          <Box sx={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: '4px' }}>
              <thead>
                <tr>
                  <th style={{ 
                    textAlign: 'left', 
                    padding: '8px 12px',
                    fontWeight: 700,
                    fontSize: '0.875rem',
                    color: 'var(--joy-palette-text-primary)',
                    minWidth: '180px'
                  }}>
                    Name (Relationship)
                  </th>
                  {Array.from({ length: 12 }, (_, i) => i + 1).map(house => (
                    <th key={house} style={{ 
                      textAlign: 'center', 
                      padding: '8px 4px',
                      fontWeight: 700,
                      fontSize: '0.75rem',
                      color: 'var(--joy-palette-text-secondary)',
                      minWidth: '50px'
                    }}>
                      <div>H{house}</div>
                      <div style={{ fontSize: '0.65rem', fontWeight: 400, marginTop: '2px' }}>
                        {HOUSE_DESCRIPTIONS[house]}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {/* Self (Main Profile) */}
                {astroData && mainUserHouseScores && (
                  <tr>
                    <td style={{ 
                      padding: '8px 12px',
                      fontWeight: 600,
                      fontSize: '0.875rem',
                      color: 'var(--joy-palette-primary-700)',
                      backgroundColor: 'var(--joy-palette-primary-50)',
                      borderRadius: '4px'
                    }}>
                      {mainUserFullName || 'You'} <span style={{ fontWeight: 400, color: 'var(--joy-palette-text-tertiary)' }}>(Self)</span>
                    </td>
                    {Array.from({ length: 12 }, (_, i) => i + 1).map(house => {
                      const score = mainUserHouseScores[house] || 0;
                      const color = score >= 15 ? '#2e7d32' : score >= 0 ? '#666' : score >= -15 ? '#ed6c02' : '#d32f2f';
                      const bgColor = score >= 15 ? '#e8f5e9' : score >= 0 ? '#f5f5f5' : score >= -15 ? '#fff3e0' : '#ffebee';
                      
                      return (
                        <td key={house} style={{ 
                          textAlign: 'center',
                          padding: '4px',
                        }}>
                          <Chip
                            size="sm"
                            sx={{
                              fontSize: '0.7rem',
                              fontWeight: 600,
                              minWidth: '45px',
                              color: color,
                              bgcolor: bgColor,
                              border: '1px solid',
                              borderColor: color + '40',
                            }}
                          >
                            {score >= 0 ? '+' : ''}{score.toFixed(0)}
                          </Chip>
                        </td>
                      );
                    })}
                  </tr>
                )}
                
                {/* Family Members (Direct only) */}
                {familyProfiles.filter(p => isDirectFamily(p.relationship)).map(profile => {
                  const memberScores = familyHouseScores[profile.id];
                  
                  return (
                    <tr key={profile.id}>
                      <td style={{ 
                        padding: '8px 12px',
                        fontSize: '0.875rem',
                        color: 'var(--joy-palette-text-primary)'
                      }}>
                        {profile.full_name} <span style={{ fontWeight: 400, color: 'var(--joy-palette-text-tertiary)' }}>({profile.relationship})</span>
                      </td>
                      {!memberScores ? (
                        <td colSpan={12} style={{ 
                          textAlign: 'center',
                          padding: '8px',
                          color: 'var(--joy-palette-warning-500)',
                          fontSize: '0.75rem',
                          fontStyle: 'italic'
                        }}>
                          ⚠️ Chart not cached - please recalculate
                        </td>
                      ) : (
                        Array.from({ length: 12 }, (_, i) => i + 1).map(house => {
                          const score = memberScores[house] || 0;
                          const color = score >= 15 ? '#2e7d32' : score >= 0 ? '#666' : score >= -15 ? '#ed6c02' : '#d32f2f';
                          const bgColor = score >= 15 ? '#e8f5e9' : score >= 0 ? '#f5f5f5' : score >= -15 ? '#fff3e0' : '#ffebee';
                          
                          return (
                            <td key={house} style={{ 
                              textAlign: 'center',
                              padding: '4px',
                            }}>
                              <Chip
                                size="sm"
                                sx={{
                                  fontSize: '0.7rem',
                                  fontWeight: 600,
                                  minWidth: '45px',
                                  color: color,
                                  bgcolor: bgColor,
                                  border: '1px solid',
                                  borderColor: color + '40',
                                }}
                              >
                                {score >= 0 ? '+' : ''}{score.toFixed(0)}
                              </Chip>
                            </td>
                          );
                        })
                      )}
                    </tr>
                  );
                })}
                
                {/* Cumulative Row */}
                <tr style={{ borderTop: '2px solid var(--joy-palette-divider)' }}>
                  <td style={{ 
                    padding: '12px 12px 8px 12px',
                    fontWeight: 700,
                    fontSize: '0.875rem',
                    color: 'var(--joy-palette-primary-700)',
                    backgroundColor: 'var(--joy-palette-primary-100)',
                    borderRadius: '4px'
                  }}>
                    Cumulative Average
                  </td>
                  {Array.from({ length: 12 }, (_, i) => i + 1).map(house => {
                    const { avg, count } = cumulativeScores[house] || { avg: 0, count: 0 };
                    if (count === 0) return <td key={house} />;
                    
                    const color = avg >= 15 ? '#2e7d32' : avg >= 0 ? '#666' : avg >= -15 ? '#ed6c02' : '#d32f2f';
                    const bgColor = avg >= 15 ? '#c8e6c9' : avg >= 0 ? '#e0e0e0' : avg >= -15 ? '#ffe0b2' : '#ffcdd2';
                    
                    return (
                      <td key={house} style={{ 
                        textAlign: 'center',
                        padding: '8px 4px 4px 4px',
                      }}>
                        <Chip
                          size="md"
                          sx={{
                            fontSize: '0.8rem',
                            fontWeight: 700,
                            minWidth: '45px',
                            color: color,
                            bgcolor: bgColor,
                            border: '2px solid',
                            borderColor: color,
                          }}
                        >
                          {avg >= 0 ? '+' : ''}{avg.toFixed(0)}
                        </Chip>
                      </td>
                    );
                  })}
                </tr>
              </tbody>
            </table>
          </Box>
        </Sheet>
      )}

      {/* ===== Matrix Modal (Expanded View) ===== */}
      <Modal 
        open={matrixModalOpen} 
        onClose={() => setMatrixModalOpen(false)} 
        sx={{ zIndex: 99999 }}
      >
        <ModalDialog
          sx={{
            maxWidth: '98vw',
            width: '98vw',
            height: '95vh',
            display: 'flex',
            flexDirection: 'column',
            p: 2,
            zIndex: 100000,
          }}
        >
          <ModalClose />
          <Typography level="h4" sx={{ mb: 1, color: 'primary.700', fontWeight: 700 }}>
            📊 Family House Scores Matrix
          </Typography>
          <Typography level="body-xs" sx={{ mb: 2, color: 'text.secondary' }}>
            Complete house-wise strength analysis for self and direct family members. Last row shows cumulative average.
          </Typography>
          
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <table style={{ 
              borderCollapse: 'separate', 
              borderSpacing: '4px',
              fontSize: '0.85rem',
            }}>
              <thead>
                <tr>
                  <th style={{ 
                    textAlign: 'left', 
                    padding: '8px 12px',
                    fontWeight: 700,
                    fontSize: '0.85rem',
                    color: 'var(--joy-palette-text-primary)',
                    minWidth: '180px',
                  }}>
                    Name (Relationship)
                  </th>
                  {Array.from({ length: 12 }, (_, i) => i + 1).map(house => (
                    <th key={house} style={{ 
                      textAlign: 'center', 
                      padding: '8px 6px',
                      fontWeight: 700,
                      fontSize: '0.8rem',
                      color: 'var(--joy-palette-text-secondary)',
                      minWidth: '60px'
                    }}>
                      <div>H{house}</div>
                      <div style={{ fontSize: '0.7rem', fontWeight: 400, marginTop: '2px', lineHeight: '1.2' }}>
                        {HOUSE_DESCRIPTIONS[house]}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {/* Self (Main Profile) */}
                {astroData && mainUserHouseScores && (
                  <tr>
                    <td style={{ 
                      padding: '8px 12px',
                      fontWeight: 600,
                      fontSize: '0.85rem',
                      color: 'var(--joy-palette-primary-700)',
                      backgroundColor: 'var(--joy-palette-primary-50)',
                      borderRadius: '4px',
                    }}>
                      {mainUserFullName || 'You'} <span style={{ fontWeight: 400, color: 'var(--joy-palette-text-tertiary)' }}>(Self)</span>
                    </td>
                    {Array.from({ length: 12 }, (_, i) => i + 1).map(house => {
                      const score = mainUserHouseScores[house] || 0;
                      const color = score >= 15 ? '#2e7d32' : score >= 0 ? '#666' : score >= -15 ? '#ed6c02' : '#d32f2f';
                      const bgColor = score >= 15 ? '#e8f5e9' : score >= 0 ? '#f5f5f5' : score >= -15 ? '#fff3e0' : '#ffebee';
                      
                      return (
                        <td key={house} style={{ 
                          textAlign: 'center',
                          padding: '4px',
                        }}>
                          <Chip
                            size="md"
                            sx={{
                              fontSize: '0.8rem',
                              fontWeight: 600,
                              minWidth: '50px',
                              color: color,
                              bgcolor: bgColor,
                              border: '1px solid',
                              borderColor: color + '40',
                            }}
                          >
                            {score >= 0 ? '+' : ''}{score.toFixed(0)}
                          </Chip>
                        </td>
                      );
                    })}
                  </tr>
                )}
                
                {/* Family Members (Direct only) */}
                {familyProfiles.filter(p => isDirectFamily(p.relationship)).map(profile => {
                  const memberScores = familyHouseScores[profile.id];
                  
                  return (
                    <tr key={profile.id}>
                      <td style={{ 
                        padding: '8px 12px',
                        fontSize: '0.85rem',
                        color: 'var(--joy-palette-text-primary)',
                      }}>
                        {profile.full_name} <span style={{ fontWeight: 400, color: 'var(--joy-palette-text-tertiary)' }}>({profile.relationship})</span>
                      </td>
                      {!memberScores ? (
                        <td colSpan={12} style={{ 
                          textAlign: 'center',
                          padding: '8px',
                          color: 'var(--joy-palette-warning-500)',
                          fontSize: '0.75rem',
                          fontStyle: 'italic'
                        }}>
                          ⚠️ Chart not cached - please recalculate
                        </td>
                      ) : (
                        Array.from({ length: 12 }, (_, i) => i + 1).map(house => {
                          const score = memberScores[house] || 0;
                          const color = score >= 15 ? '#2e7d32' : score >= 0 ? '#666' : score >= -15 ? '#ed6c02' : '#d32f2f';
                          const bgColor = score >= 15 ? '#e8f5e9' : score >= 0 ? '#f5f5f5' : score >= -15 ? '#fff3e0' : '#ffebee';
                          
                          return (
                            <td key={house} style={{ 
                              textAlign: 'center',
                              padding: '4px',
                            }}>
                              <Chip
                                size="md"
                                sx={{
                                  fontSize: '0.8rem',
                                  fontWeight: 600,
                                  minWidth: '50px',
                                  color: color,
                                  bgcolor: bgColor,
                                  border: '1px solid',
                                  borderColor: color + '40',
                                }}
                              >
                                {score >= 0 ? '+' : ''}{score.toFixed(0)}
                              </Chip>
                            </td>
                          );
                        })
                      )}
                    </tr>
                  );
                })}
                
                {/* Cumulative Row */}
                {cumulativeScores && (
                  <tr style={{ borderTop: '2px solid var(--joy-palette-divider)' }}>
                    <td style={{ 
                      padding: '10px 12px',
                      fontWeight: 700,
                      fontSize: '0.85rem',
                      color: 'var(--joy-palette-primary-700)',
                      backgroundColor: 'var(--joy-palette-primary-100)',
                      borderRadius: '4px',
                    }}>
                      Cumulative Average
                    </td>
                    {Array.from({ length: 12 }, (_, i) => i + 1).map(house => {
                      const { avg, count } = cumulativeScores[house] || { avg: 0, count: 0 };
                      if (count === 0) return <td key={house} />;
                      
                      const color = avg >= 15 ? '#2e7d32' : avg >= 0 ? '#666' : avg >= -15 ? '#ed6c02' : '#d32f2f';
                      const bgColor = avg >= 15 ? '#c8e6c9' : avg >= 0 ? '#e0e0e0' : avg >= -15 ? '#ffe0b2' : '#ffcdd2';
                      
                      return (
                        <td key={house} style={{ 
                          textAlign: 'center',
                          padding: '6px 4px',
                        }}>
                          <Chip
                            size="md"
                            sx={{
                              fontSize: '0.85rem',
                              fontWeight: 700,
                              minWidth: '50px',
                              color: color,
                              bgcolor: bgColor,
                              border: '2px solid',
                              borderColor: color,
                            }}
                          >
                            {avg >= 0 ? '+' : ''}{avg.toFixed(0)}
                          </Chip>
                        </td>
                      );
                    })}
                  </tr>
                )}
              </tbody>
            </table>
          </Box>
        </ModalDialog>
      </Modal>

      {/* ===== OLD SECTION: Cumulative Family House Analysis (REMOVED - replaced by matrix above) ===== */}
      {/* Keeping this comment as a marker for the removed card-based cumulative view */}


      {/* ===== NEW SECTION 2: Chart Overlay Predictions ===== */}
      {chartOverlays.length > 0 && (
        <Sheet
          variant="outlined"
          sx={{
            borderRadius: 'md',
            p: 3,
            bgcolor: 'background.level1',
            border: '2px solid',
            borderColor: 'warning.300',
          }}
        >
          <Typography level="h4" sx={{ mb: 2, color: 'warning.700', fontWeight: 700 }}>
            🔮 Family Chart Overlays - Planetary Influences
          </Typography>
          <Typography level="body-sm" sx={{ mb: 2, color: 'text.secondary' }}>
            When a family member's planet falls in your house or on your Moon, it creates a specific energetic dynamic.
            These overlays reveal how their presence affects your life areas.
          </Typography>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {chartOverlays.map((overlay, idx) => {
              const sentimentColors = {
                positive: 'success',
                negative: 'danger',
                neutral: 'neutral',
                mixed: 'warning',
              } as const;
              
              const color = sentimentColors[overlay.prediction.sentiment];
              
              return (
                <Sheet
                  key={idx}
                  variant="soft"
                  color={color}
                  sx={{
                    p: 2,
                    borderRadius: 'md',
                    border: '1px solid',
                    borderColor: `${color}.300`,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Chip size="sm" variant="solid" color={color} sx={{ fontWeight: 700 }}>
                      {overlay.familyMemberName}
                    </Chip>
                    <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                      ({overlay.relationship})
                    </Typography>
                    <Chip size="sm" variant="outlined" sx={{ fontWeight: 600 }}>
                      {overlay.planet} → {overlay.targetIsMoon ? 'Your Moon' : `Your H${overlay.targetHouse}`}
                    </Chip>
                  </Box>
                  
                  <Typography level="title-sm" sx={{ fontWeight: 700, mb: 0.5, color: `${color}.800` }}>
                    {overlay.prediction.title}
                  </Typography>
                  
                  <Typography level="body-sm" sx={{ color: 'text.primary', lineHeight: 1.6 }}>
                    {overlay.prediction.prediction}
                  </Typography>
                </Sheet>
              );
            })}
          </Box>
        </Sheet>
      )}

      {/* ===== EXISTING SECTION: House-wise Family Relationships ===== */}
      <Typography level="h4" sx={{ color: 'primary.700', fontWeight: 700 }}>
        🏠 House-wise Family Relationships
      </Typography>
      
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: 2,
        }}
      >
        {Array.from({ length: 12 }, (_, i) => (i + 1) as HouseNum).map(houseNum => {
          const relationships = HOUSE_TO_FAMILY[houseNum] || [];
          const houseTitle = getHouseFamilyTitle(houseNum);

          return (
            <Sheet
              key={houseNum}
              variant="outlined"
              sx={{
                borderRadius: 'md',
                p: 2,
                display: 'flex',
                flexDirection: 'column',
                gap: 1.5,
                transition: 'all 0.2s',
                '&:hover': {
                  boxShadow: 'sm',
                  borderColor: 'primary.300',
                },
                minHeight: '180px',
              }}
            >
              {/* House Strength Info */}
              {d1HouseScores && d1HouseScores[houseNum] != null && (
                <Box sx={{ mb: 1.5 }}>
                  {/* Top Band: House Strength & Planet Impact */}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-around',
                      gap: 2,
                      py: 1,
                      px: 1,
                      borderRadius: 'sm',
                      bgcolor: (bhavaBalaRupas?.[houseNum] ?? d1HouseScores[houseNum]) > 8 ? 'success.50' :
                               (bhavaBalaRupas?.[houseNum] ?? d1HouseScores[houseNum]) > 0 ? 'success.100' :
                               (bhavaBalaRupas?.[houseNum] ?? d1HouseScores[houseNum]) < -8 ? 'danger.100' :
                               (bhavaBalaRupas?.[houseNum] ?? d1HouseScores[houseNum]) < 0 ? 'danger.50' : 'neutral.50',
                    }}
                  >
                    {/* House Strength */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1 }}>
                      <Typography level="body-xs" sx={{ color: 'text.tertiary', fontSize: '0.65rem', fontWeight: 500, mb: 0.25 }}>
                        House Strength
                      </Typography>
                      <Typography
                        level="h4"
                        sx={{
                          fontWeight: 700,
                          fontSize: '1.25rem',
                          color: (bhavaBalaRupas?.[houseNum] ?? d1HouseScores[houseNum]) > 8 ? 'success.700' :
                                 (bhavaBalaRupas?.[houseNum] ?? d1HouseScores[houseNum]) > 0 ? 'success.600' :
                                 (bhavaBalaRupas?.[houseNum] ?? d1HouseScores[houseNum]) < -8 ? 'danger.700' :
                                 (bhavaBalaRupas?.[houseNum] ?? d1HouseScores[houseNum]) < 0 ? 'danger.600' : 'neutral.600',
                        }}
                      >
                        {(bhavaBalaRupas?.[houseNum] ?? d1HouseScores[houseNum]) > 0 ? '+' : ''}
                        {(bhavaBalaRupas?.[houseNum] ?? d1HouseScores[houseNum]).toFixed(1)}%
                      </Typography>
                    </Box>

                    {/* Divider */}
                    <Box sx={{ width: '1px', height: '32px', bgcolor: 'divider' }} />

                    {/* Planet Impact */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1 }}>
                      <Typography level="body-xs" sx={{ color: 'text.tertiary', fontSize: '0.65rem', fontWeight: 500, mb: 0.25 }}>
                        Planet Impact
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography
                          level="h4"
                          sx={{
                            fontWeight: 700,
                            fontSize: '1.25rem',
                            color: d1HouseScores[houseNum] > 8 ? 'success.700' :
                                   d1HouseScores[houseNum] > 0 ? 'success.600' :
                                   d1HouseScores[houseNum] < -8 ? 'danger.700' :
                                   d1HouseScores[houseNum] < 0 ? 'danger.600' : 'neutral.600',
                          }}
                        >
                          {d1HouseScores[houseNum] >= 0 ? '+' : ''}{d1HouseScores[houseNum].toFixed(1)}%
                        </Typography>
                        {/* Interpretive Chip (Ideal/Strong/Intense/etc.) */}
                        {bhavaBalaRupas?.[houseNum] != null && (() => {
                          const bhavaBala = bhavaBalaRupas[houseNum];
                          const v = d1HouseScores[houseNum];
                          const highBhava = bhavaBala > 50;
                          const positiveImpact = v >= 5;
                          const negativeImpact = v <= -5;
                          const neutralImpact = !positiveImpact && !negativeImpact;
                          
                          let chipLabel = '';
                          let chipColor = '';
                          let chipBg = '';
                          let chipBorder = '';
                          
                          if (highBhava && positiveImpact) {
                            chipLabel = '✅ Ideal';
                            chipColor = '#16a34a';
                            chipBg = '#dcfce720';
                            chipBorder = '1px solid #86efac50';
                          } else if (highBhava && negativeImpact) {
                            chipLabel = '⚠️ Intense';
                            chipColor = '#dc2626';
                            chipBg = '#fee2e220';
                            chipBorder = '1px solid #fca5a550';
                          } else if (highBhava && neutralImpact) {
                            chipLabel = '🔵 Strong';
                            chipColor = '#2563eb';
                            chipBg = '#dbeafe20';
                            chipBorder = '1px solid #93c5fd50';
                          } else if (!highBhava && positiveImpact) {
                            chipLabel = '🟡 Limited';
                            chipColor = '#ca8a04';
                            chipBg = '#fef9c320';
                            chipBorder = '1px solid #fde04750';
                          } else if (!highBhava && negativeImpact) {
                            chipLabel = '❌ Weak';
                            chipColor = '#dc2626';
                            chipBg = '#fee2e220';
                            chipBorder = '1px solid #fca5a550';
                          } else if (!highBhava && neutralImpact) {
                            chipLabel = '⚪ Neutral';
                            chipColor = '#64748b';
                            chipBg = '#f1f5f920';
                            chipBorder = '1px solid #cbd5e150';
                          }
                          
                          if (!chipLabel) return null;
                          
                          return (
                            <Chip
                              size="sm"
                              sx={{
                                fontSize: '0.6rem',
                                minHeight: 16,
                                px: 0.4,
                                fontWeight: 600,
                                bgcolor: chipBg,
                                color: chipColor,
                                border: chipBorder,
                              }}
                            >
                              {chipLabel}
                            </Chip>
                          );
                        })()}
                      </Box>
                    </Box>
                  </Box>

                  {/* House Lord Chip */}
                  {astroData?.placements && (() => {
                    // Get sign of this house from cusps
                    const signIdx = Math.floor((astroData.cusps?.[houseNum - 1] ?? 0) / 30);
                    const SIGN_LORDS_LOCAL = ['Mars', 'Venus', 'Mercury', 'Moon', 'Sun', 'Mercury', 'Venus', 'Mars', 'Jupiter', 'Saturn', 'Saturn', 'Jupiter'];
                    const lord = SIGN_LORDS_LOCAL[signIdx];
                    const lordPlacement = astroData.placements[lord];
                    
                    if (!lordPlacement) return null;
                    
                    return (
                      <Chip
                        size="sm"
                        variant="soft"
                        color="primary"
                        sx={{ mt: 0.5, fontSize: '0.65rem', px: 0.5, fontWeight: 600 }}
                      >
                        Lord {lord} in H{lordPlacement.house}
                      </Chip>
                    );
                  })()}
                </Box>
              )}

              {/* House Header */}
              <Box 
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between',
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  pb: 1,
                }}
              >
                <Typography 
                  level="title-sm" 
                  sx={{ 
                    fontWeight: 700,
                    color: 'primary.700',
                    letterSpacing: '0.3px',
                  }}
                >
                  H{houseNum} • {houseTitle}
                </Typography>
                <Typography 
                  level="body-xs" 
                  sx={{ 
                    color: 'text.tertiary',
                    fontStyle: 'italic',
                  }}
                >
                  ({relationships.length} {relationships.length === 1 ? 'relationship' : 'relationships'})
                </Typography>
              </Box>

              {/* Relationships */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {relationships.length === 0 ? (
                  <Typography level="body-xs" sx={{ color: 'text.tertiary', fontStyle: 'italic' }}>
                    No traditional relationships for this house
                  </Typography>
                ) : (
                  relationships.map(rel => {
                    const profile = findFamilyMember(rel.dbValue);
                    const hasData = hasCompleteData(profile);

                    return (
                      <Box 
                        key={rel.dbValue}
                        sx={{ 
                          display: 'flex', 
                          flexDirection: 'column',
                          gap: 0.5,
                          p: 1,
                          borderRadius: 'sm',
                          bgcolor: profile ? 'success.50' : 'neutral.50',
                          border: '1px solid',
                          borderColor: profile ? 'success.200' : 'neutral.200',
                        }}
                      >
                        {/* Relationship Name */}
                        <Typography 
                          level="body-sm" 
                          sx={{ 
                            fontWeight: 600,
                            color: profile ? 'success.700' : 'text.primary',
                          }}
                        >
                          {rel.displayName}
                          {profile && profile.full_name && ` (${profile.full_name})`}
                        </Typography>

                        {/* Life Area Chips */}
                        {hasData && profile ? (() => {
                          const memberScores = familyHouseScores[profile.id];
                          if (!memberScores) {
                            return (
                              <Typography level="body-xs" sx={{ color: 'warning.600', fontStyle: 'italic' }}>
                                ⚠️ Chart not cached - please recalculate this member's chart in Birth Details tab
                              </Typography>
                            );
                          }

                          const lifeAreaScores = calculateFamilyMemberLifeAreas(memberScores);
                          
                          return (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {lifeAreaScores.map(({ lifeArea, displayText, score }) => (
                                <Chip
                                  key={lifeArea}
                                  size="sm"
                                  variant="soft"
                                  color={getChipColor(score)}
                                  sx={{ fontSize: '0.65rem', py: 0.25, px: 0.5, fontWeight: 600 }}
                                >
                                  {lifeArea}: {displayText}
                                </Chip>
                              ))}
                            </Box>
                          );
                        })() : !profile ? (
                          <Typography 
                            level="body-xs" 
                            sx={{ 
                              color: 'primary.600',
                              cursor: 'pointer',
                              textDecoration: 'underline',
                              '&:hover': { color: 'primary.700' }
                            }}
                            onClick={() => onNavigateToBirthDetails?.()}
                          >
                            + Add in Birth Details
                          </Typography>
                        ) : (
                          <Typography level="body-xs" sx={{ color: 'warning.600', fontStyle: 'italic' }}>
                            Incomplete birth data
                          </Typography>
                        )}

                        {/* Relationship Description (tooltip text) */}
                        <Typography 
                          level="body-xs" 
                          sx={{ 
                            color: 'text.tertiary', 
                            fontStyle: 'italic',
                            fontSize: '0.7rem',
                          }}
                        >
                          {rel.description}
                        </Typography>
                      </Box>
                    );
                  })
                )}
              </Box>
            </Sheet>
          );
        })}
      </Box>
    </Box>
  );
}
